/** @jsx jsx */

import { Fragment, SyntheticEvent, useCallback } from 'react';
import { capitalize, isEmpty, values } from 'lodash';

import { Icon } from '@zapier/zinnia-icons';
import {
  Alert,
  Link as ZinniaLink,
  Text,
  Tooltip,
  TooltipFocusableTrigger,
} from '@zapier/design-system';
import { ZapierLogo } from '@zapier/design-system-patterns';
import { useLink } from '@zapier/common-hooks';
import { jsx, css } from '@emotion/core';
import { Icon as ZinniaIcon } from '@zapier/zinnia-icons';

import ActionsDropdownMenu from 'app/developer-v3/components/ActionsDropdownMenu';
import ByCliToolMessage from 'app/developer-v3/components/ByCliToolMessage';
import ClickableTable from 'app/developer-v3/components/ClickableTable';
import HelpBox from 'app/developer-v3/components/HelpBox';
import Link from 'app/common/components/Link';
import LockedNavCog from 'app/developer-v3/components/LockedNavCog';
import ManagerLayout from 'app/developer-v3/layouts/ManagerLayout';
import { BASE_DOC_URL_VB, NEW_SERVICE_FIELD } from 'app/developer-v3/constants';
import {
  CurrentVersionReadOnlyReason,
  useActiveProfile,
  useCurrentVersionReadOnlyReason,
} from 'app/developer-v3/hooks';
import {
  getTriggerUrl,
  isLastDescendantNodeClicked,
} from 'app/developer-v3/utils';

import { isExternalTrigger } from './utils';

import type { AppDefinitionProps, AppSummary } from 'app/developer-v3/types';
import type { TriggerSchema } from 'app/developer-v3/platformSchema/trigger';
import type { VersionParams } from 'app/developer-v3/types/version';

const Styles = {
  labelTextWrapper: css`
    align-items: center;
    display: flex;
    gap: 5px;
  `,
};

type Props = AppDefinitionProps & {
  appSummary: AppSummary;
  deleteTrigger: (triggerKey: string) => void;
  params: VersionParams;
  triggers: Record<string, TriggerSchema>;
};

function TriggersPage({
  appSummary,
  deleteTrigger,
  params,
  triggers: triggersObj,
}: Props) {
  const { routeTo } = useLink();

  const currentVersionReadOnlyReason = useCurrentVersionReadOnlyReason();
  const isReadOnly = !!currentVersionReadOnlyReason;
  const isNotAdmin =
    currentVersionReadOnlyReason === CurrentVersionReadOnlyReason.NOT_ADMIN;
  const { isLoading: isLoadingProfile, activeProfile } = useActiveProfile();
  const isStaff = !isLoadingProfile && activeProfile?.isStaff;

  const { isGUIIntegration } = appSummary;

  const triggers = values(triggersObj);

  const onClickRow = useCallback(
    (event: SyntheticEvent<HTMLElement>, index: number) => {
      const trigger = triggers[index];

      if (isExternalTrigger(trigger)) {
        const actionUrl = trigger.links?.actionUrl;
        if (isStaff && actionUrl) {
          routeTo(actionUrl);
        }
      } else if (
        isGUIIntegration &&
        !isNotAdmin &&
        !isLastDescendantNodeClicked(event)
      ) {
        routeTo(
          getTriggerUrl({
            id: params.id,
            version: params.version,
            triggerKey: String(triggers[index].key),
          })
        );
      }
    },
    [
      triggers,
      isStaff,
      routeTo,
      isGUIIntegration,
      isNotAdmin,
      params.id,
      params.version,
    ]
  );

  const getCellModifiers = useCallback(
    (iCol: number, iRow: number) => {
      const trigger = triggers[iRow];

      return {
        darken: iCol < 3,
        disabled: isExternalTrigger(trigger)
          ? !isStaff
          : !isGUIIntegration || isNotAdmin,
      };
    },
    [isGUIIntegration, isNotAdmin, isStaff, triggers]
  );

  const hasOnlyExternalTriggers = triggers.every(isExternalTrigger);

  const header = [
    { title: 'Name', hasSort: true },
    { title: 'Key', hasSort: false },
    { title: 'Source', hasSort: false },
    { title: 'Visibility in Editor', hasSort: true },
    { title: 'Origin', hasSort: true },
    { title: '', hasSort: false },
  ];
  if (hasOnlyExternalTriggers) {
    header.splice(2, 1);
  }

  const body = triggers.map(trigger => {
    const row = [
      // Name column (Trigger Label)
      {
        value: trigger.display.label,
        element: (
          <Fragment>
            {!isGUIIntegration ? (
              <Tooltip
                allowMultiline={true}
                content="This integration is built with the CLI tool."
                placement="bottom start"
              >
                <TooltipFocusableTrigger tag="div">
                  <div css={Styles.labelTextWrapper}>
                    <Fragment>
                      <Text type="Body4">{trigger.display.label}</Text>
                      {isExternalTrigger(trigger) ? (
                        <Fragment>
                          <div
                            style={{
                              alignContent: 'center',
                              backgroundColor: '#201515',
                              borderRadius: 5,
                              display: 'grid',
                              height: 20,
                              justifyContent: 'center',
                              width: 70,
                            }}
                          >
                            <ZapierLogo
                              height={14}
                              variant="wordmark-horizontal-inverse"
                            />
                          </div>
                          {!isStaff && <Icon name="actionLock" size={20} />}
                        </Fragment>
                      ) : (
                        !isGUIIntegration && (
                          <Icon name="actionLock" size={20} />
                        )
                      )}
                    </Fragment>
                  </div>
                </TooltipFocusableTrigger>
              </Tooltip>
            ) : (
              <div css={Styles.labelTextWrapper}>
                <Fragment>
                  <Text type="Body4">{trigger.display.label}</Text>
                  {isExternalTrigger(trigger) ? (
                    <Fragment>
                      <div
                        style={{
                          alignContent: 'center',
                          backgroundColor: '#201515',
                          borderRadius: 5,
                          display: 'grid',
                          height: 20,
                          justifyContent: 'center',
                          width: 70,
                        }}
                      >
                        <ZapierLogo
                          height={14}
                          variant="wordmark-horizontal-inverse"
                        />
                      </div>
                      {!isStaff && <Icon name="actionLock" size={20} />}
                    </Fragment>
                  ) : (
                    !isGUIIntegration && <Icon name="actionLock" size={20} />
                  )}
                </Fragment>
              </div>
            )}
            <Text tag="p">{trigger.display.description}</Text>
          </Fragment>
        ),
      },
      // Key column
      { value: trigger.key, element: trigger.key },
      // Source column
      {
        value: isExternalTrigger(trigger)
          ? null
          : trigger.operation.type || 'polling',
        element: (
          <Text>
            {isExternalTrigger(trigger)
              ? null
              : capitalize(trigger.operation.type || 'polling')}
          </Text>
        ),
      },
      // Visibility column
      {
        value: trigger.display.hidden ? 'Hidden' : 'Shown',
        element: <Text>{trigger.display.hidden ? 'Hidden' : 'Shown'}</Text>,
      },
      // Origin column
      {
        value: isExternalTrigger(trigger)
          ? 'External trigger'
          : isGUIIntegration
          ? 'UI'
          : 'CLI',
        element: (
          <Text>
            {isExternalTrigger(trigger)
              ? 'External trigger'
              : isGUIIntegration
              ? 'UI'
              : 'CLI'}
          </Text>
        ),
      },
      // Actions column
      {
        value: '',
        element: isNotAdmin ? (
          <LockedNavCog message="Admin access only" />
        ) : (
          isGUIIntegration &&
          !isReadOnly && (
            <ActionsDropdownMenu
              confirmationText="Delete"
              items={[
                {
                  content: (
                    <Fragment>
                      <ZinniaIcon name="actionEdit" size={16} /> Edit
                    </Fragment>
                  ),
                  onChoose: () =>
                    routeTo(
                      getTriggerUrl({
                        id: params.id,
                        version: params.version,
                        triggerKey: trigger.key,
                      })
                    ),
                  value: 'edit',
                },
                {
                  isConfirmationRequired: true,
                  isDanger: true,
                  content: (
                    <Fragment>
                      <ZinniaIcon name="actionTrash" size={16} /> Delete
                    </Fragment>
                  ),
                  modalTextContent:
                    'Are you sure you want to delete this trigger?',
                  modalTitle: `Delete ${trigger.display.label} trigger?`,
                  onChoose: () => deleteTrigger(trigger.key),
                  value: 'delete',
                },
              ]}
            />
          )
        ),
      },
    ];

    if (hasOnlyExternalTriggers) {
      row.splice(2, 1);
    }

    return row;
  });

  return (
    <ManagerLayout
      afterHeader={
        <Fragment>
          <ZinniaLink
            isDisabled={!isGUIIntegration || isReadOnly}
            variant="primary"
            href={getTriggerUrl({
              id: params.id,
              version: params.version,
              triggerKey: NEW_SERVICE_FIELD,
            })}
            iconPosition="start"
          >
            <ZinniaIcon name="formAdd" />
            Add Trigger
          </ZinniaLink>
        </Fragment>
      }
      title="Triggers"
    >
      {!isGUIIntegration && (
        <Alert>
          <ByCliToolMessage theme="light" />
        </Alert>
      )}
      {isGUIIntegration && isEmpty(triggers) && (
        <HelpBox
          description={
            <Text tag="span">
              Triggers GET or read data from your API to start workflows. Add
              triggers for new and updated items in your app so users can use
              the data in other apps.{' '}
              <Link href={`${BASE_DOC_URL_VB}/build/trigger`} target="_blank">
                Learn more
              </Link>
              .
            </Text>
          }
          heading="Add Your First Trigger"
        />
      )}
      {!isEmpty(triggers) && (
        <ClickableTable
          body={body}
          getCellModifiers={getCellModifiers}
          header={header}
          onClickRow={onClickRow}
          rowAriaLabel="Click to edit this trigger"
        />
      )}
    </ManagerLayout>
  );
}

export default TriggersPage;
